import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { DatePicker, Select, TextInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import type { RequestToReopen } from "pages/liens/LienTypes";
import type { FieldErrors } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import type { SingleValue } from "react-select";
import { DATE_TO_STRING, STRING_TO_DATE } from "utils/date-utils";
import { useConstants } from "utils/hooks";

type OptionType = {
  value: string | null;
  label: string;
};

type RequestToReopenModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RequestToReopen) => Promise<void>;
  lienId: number;
  organizationId: number;
  title?: string;
};

const FormErrors = ({ errors }: { errors: FieldErrors<RequestToReopen> }) => {
  if (Object.keys(errors).length === 0) return null;

  return (
    <div className="error-summary">
      {Object.entries(errors).map(([field, error]) => (
        <div key={field} className="error-message">
          {error?.message || `${field} is invalid`}
        </div>
      ))}
    </div>
  );
};

const RequestToReopenModal = ({
  isOpen,
  onClose,
  onSubmit,
  lienId,
  organizationId,
  title = "Request to Reopen",
}: RequestToReopenModalProps) => {
  const { constants } = useConstants();

  const decisionOptions =
    constants?.RequestToReopenDecision?.filter(
      (option) => option.value !== null,
    ) || [];

  const officeLevelOptions =
    constants?.OfficeLevelChoices?.filter((option) => option.value !== null) ||
    [];

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RequestToReopen>({
    defaultValues: {
      id: 0,
      lien: lienId,
      organization: organizationId,
      request_to_reopen_date: "",
      lien_amount: "",
      request_to_reopen_amount: "",
      request_to_reopen_decision:
        constants?.RequestToReopenDecision?.[0]?.value ?? undefined,
      request_to_reopen_savings: "",
      office_level: constants?.OfficeLevelChoices?.[0]?.value ?? undefined,
    },
    mode: "onBlur",
  });

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      await onSubmit(data);
      onClose();
      reset();
    } catch (error) {
      console.error("Form submission error:", error);
    }
  });

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <div className="flex jc-c">
          <Button onClick={handleFormSubmit}>Save</Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      }
    >
      <Card heading={title}>
        <FormErrors errors={errors} />
        <Controller
          name="request_to_reopen_date"
          control={control}
          rules={{
            required: "Request date is required",
            validate: (value) => {
              if (!value) return "Request date is required";
              const date = STRING_TO_DATE(value);
              if (!date) return "Invalid date format";
              return true;
            },
          }}
          render={({ field: { onChange, value, ...field } }) => (
            <DatePicker
              allowFutureDates
              {...field}
              initialDate={STRING_TO_DATE(value) ?? undefined}
              onDateChange={(date) => {
                onChange(DATE_TO_STRING(date));
              }}
              label="Request Date"
              errors={errors}
            />
          )}
        />
        <Controller
          name="lien_amount"
          control={control}
          rules={{
            required: "Lien amount is required",
            pattern: {
              value: /^-?\d*\.?\d*$/,
              message: "Please enter a valid number",
            },
          }}
          render={({ field }) => (
            <TextInput
              {...field}
              type="number"
              step="0.01"
              label="Lien Amount"
              errors={errors}
            />
          )}
        />
        <Controller
          name="request_to_reopen_amount"
          control={control}
          rules={{
            required: "Request amount is required",
            pattern: {
              value: /^-?\d*\.?\d*$/,
              message: "Please enter a valid number",
            },
          }}
          render={({ field }) => (
            <TextInput
              {...field}
              type="number"
              step="0.01"
              label="Request Amount"
              errors={errors}
            />
          )}
        />
        <Controller
          name="request_to_reopen_decision"
          control={control}
          render={({ field: { onChange, value, ...field } }) => (
            <Select
              {...field}
              label="Decision"
              options={decisionOptions}
              value={decisionOptions.find((option) => option.value === value)}
              onChange={(newValue: SingleValue<OptionType>) =>
                onChange(newValue?.value ?? undefined)
              }
              errors={errors}
            />
          )}
        />
        <Controller
          name="request_to_reopen_savings"
          control={control}
          rules={{
            pattern: {
              value: /^-?\d*\.?\d*$/,
              message: "Please enter a valid number",
            },
          }}
          render={({ field }) => (
            <TextInput
              {...field}
              type="number"
              step="0.01"
              label="Savings"
              errors={errors}
            />
          )}
        />
        <Controller
          name="office_level"
          control={control}
          rules={{ required: "Office level is required" }}
          render={({ field: { onChange, value, ...field } }) => (
            <Select
              {...field}
              label="Office Level"
              options={officeLevelOptions}
              value={officeLevelOptions.find(
                (option) => option.value === value,
              )}
              onChange={(option: OptionType | null) =>
                onChange(option?.value ?? undefined)
              }
              errors={errors}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default RequestToReopenModal;
